class ProductAccessoryBundle extends HTMLElement {
  constructor() {
    super();
    this._onFormSubmit = this._onFormSubmit.bind(this);
    this._onCheckboxChange = this._onCheckboxChange.bind(this);
    this._onVariantSelectChange = this._onVariantSelectChange.bind(this);
    this._onVariantInputChange = this._onVariantInputChange.bind(this);
    this._onOptionValueSelectionChange = this._onOptionValueSelectionChange.bind(this);
    this._onVariantChange = this._onVariantChange.bind(this);
    this._onQuantityChange = this._onQuantityChange.bind(this);
    this._onToggleMore = this._onToggleMore.bind(this);
  }

  connectedCallback() {
    this.productId = this.dataset.productId;
    this.addToCartLabel = this.dataset.addToCartLabel || 'Add to cart';
    this.moreLimit = parseInt(this.dataset.moreLimit || '4', 10);
    this._mainVariantId = this.dataset.mainVariantId;
    this._mainVariantPrice = parseInt(this.dataset.mainPrice || '0', 10) || 0;
    this._variantDataMap = this._parseVariantDataMap();
    this._variantModelMap = Object.fromEntries(
      Object.entries(this._variantDataMap).map(([variantId, variant]) => [variantId, variant.model || ''])
    );

    this._productInfo = this.closest('product-info');
    this._form = this._productInfo?.querySelector('product-form-component form[data-type="add-to-cart-form"]');
    this._quantityInput = this._form?.querySelector('.quantity__input') || this._productInfo?.querySelector('.quantity__input');

    this._bindEvents();
    this._initAccordions();
    this._initMoreToggle();

    const formVariantId = this._form?.querySelector('[name="id"]')?.value;
    if (formVariantId) this._mainVariantId = formVariantId;
    this._setActiveModel(this._variantModelMap[String(this._mainVariantId)] || this._firstModel());
  }

  disconnectedCallback() {
    this._form?.removeEventListener('submit', this._onFormSubmit, true);
    this._quantityInput?.removeEventListener('input', this._onQuantityChange);
    this._quantityInput?.removeEventListener('change', this._onQuantityChange);
    this._productInfo?.removeEventListener('change', this._onVariantInputChange, true);
    this._accordionAbort?.abort();
    this.querySelectorAll('.product-accessory-item__input').forEach((input) => {
      input.removeEventListener('change', this._onCheckboxChange);
    });
    this.querySelectorAll('[data-accessory-variant-select]').forEach((select) => {
      select.removeEventListener('change', this._onVariantSelectChange);
    });
    this.querySelector('[data-more-toggle]')?.removeEventListener('click', this._onToggleMore);
    this._variantChangeUnsubscriber?.();
    this._optionValueSelectionUnsubscriber?.();
    this._quantityUnsubscriber?.();
  }

  _parseVariantDataMap() {
    try {
      const data = JSON.parse(this.querySelector('[data-variant-model-map]')?.textContent || '{}');
      return Object.fromEntries(
        Object.entries(data).map(([variantId, variant]) => [
          variantId,
          typeof variant === 'string' ? { model: variant } : variant,
        ])
      );
    } catch (error) {
      console.warn('[ProductAccessoryBundle] Invalid variant model map.', error);
      return {};
    }
  }

  _firstModel() {
    return this.querySelector('[data-model-panel]')?.dataset.modelPanel || '';
  }

  _bindEvents() {
    this._form?.addEventListener('submit', this._onFormSubmit, true);
    this._quantityInput?.addEventListener('input', this._onQuantityChange);
    this._quantityInput?.addEventListener('change', this._onQuantityChange);
    this._productInfo?.addEventListener('change', this._onVariantInputChange, true);

    this.querySelectorAll('.product-accessory-item__input').forEach((input) => {
      input.addEventListener('change', this._onCheckboxChange);
    });

    this.querySelectorAll('[data-accessory-variant-select]').forEach((select) => {
      select.addEventListener('change', this._onVariantSelectChange);
    });

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this._optionValueSelectionUnsubscriber = subscribe(
        PUB_SUB_EVENTS.optionValueSelectionChange,
        this._onOptionValueSelectionChange
      );
      this._variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this._onVariantChange);
      this._quantityUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this._onQuantityChange);
    }
  }

  _onOptionValueSelectionChange(event) {
    const target = event?.data?.target;
    const picker = target?.closest('variant-selects');
    if (!picker || String(picker.dataset.productId || '') !== String(this.productId)) return;

    this._applyVariantForPicker(picker);
  }

  _onVariantInputChange(event) {
    const picker = event.target?.closest('variant-selects');
    if (!picker || String(picker.dataset.productId || '') !== String(this.productId)) return;

    this._applyVariantForPicker(picker);
  }

  _applyVariantForPicker(picker) {
    const selectedOptions = [...picker.querySelectorAll('.variant-option')]
      .map((group) => group.querySelector('select option[selected], input:checked')?.value)
      .filter((value) => typeof value === 'string');
    const variantEntry = Object.entries(this._variantDataMap).find(([, variant]) => {
      if (!Array.isArray(variant.options) || variant.options.length !== selectedOptions.length) return false;
      return variant.options.every((value, index) => value === selectedOptions[index]);
    });

    if (!variantEntry) return;
    const [variantId, variant] = variantEntry;
    this._applyOptimisticVariant(variantId, variant);
  }

  _applyOptimisticVariant(variantId, variant) {
    this._mainVariantId = String(variantId);
    this._mainVariantPrice = Number(variant.price) || 0;

    const variantInput = this._form?.querySelector('[name="id"]');
    if (variantInput) variantInput.value = variantId;

    const submitButton = this._form?.querySelector('.product-form__submit');
    if (submitButton) {
      submitButton.disabled = !variant.available;
      submitButton.toggleAttribute('disabled', !variant.available);
      submitButton.setAttribute('aria-disabled', String(!variant.available));
    }

    if (variant.model) this._setActiveModel(variant.model);
    this._updateProductPrice(variant);
    this._updateMainMedia(variant.featuredMediaId);
    this._updateUI();
  }

  _formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents, window.money_format);
    }
    return (Number(cents || 0) / 100).toFixed(2);
  }

  _updateProductPrice(variant) {
    const priceContainer = this._productInfo?.querySelector('product-price .price-product-container');
    if (!priceContainer) return;

    const price = this._formatMoney(variant.price);
    priceContainer.querySelectorAll('.price.price-item').forEach((element) => {
      element.textContent = price;
    });

    if (Number(variant.compareAtPrice) > Number(variant.price)) {
      const compareAtPrice = this._formatMoney(variant.compareAtPrice);
      priceContainer.querySelectorAll('.compare-at-price').forEach((element) => {
        element.textContent = compareAtPrice;
      });
    }
  }

  _updateMainMedia(featuredMediaId) {
    if (!featuredMediaId) return;
    const sectionId = this._productInfo?.dataset.section;
    if (!sectionId) return;
    this._productInfo.querySelector('media-gallery')?.setActiveMedia?.(`${sectionId}-${featuredMediaId}`, true);
  }

  _prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  _initAccordions() {
    this._accordionAbort = new AbortController();
    const { signal } = this._accordionAbort;
    this.classList.add('accordions-ready');

    this.querySelectorAll('.product-accessory-bundle__group').forEach((details) => {
      const summary = details.querySelector('summary');
      const contentAnim = details.querySelector('.product-accessory-bundle__content-anim');
      if (!summary || !contentAnim) return;

      this._setAccordionOpenState(details, contentAnim, details.hasAttribute('open'));
      summary.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          this._toggleAccordion(details);
        },
        { signal }
      );
    });
  }

  _setAccordionOpenState(details, contentAnim, open) {
    if (open) {
      details.setAttribute('open', '');
      details.classList.add('is-open');
      contentAnim.style.height = 'auto';
      contentAnim.style.opacity = '1';
      details.style.height = 'auto';
      return;
    }

    details.removeAttribute('open');
    details.classList.remove('is-open');
    contentAnim.style.height = '0px';
    contentAnim.style.opacity = '0';
    details.style.height = '';
  }

  async _toggleAccordion(details) {
    if (details.dataset.animating === 'true') return;

    const summary = details.querySelector('summary');
    const contentAnim = details.querySelector('.product-accessory-bundle__content-anim');
    const content = details.querySelector('.product-accessory-bundle__content');
    if (!summary || !contentAnim || !content) return;

    const willOpen = !details.classList.contains('is-open');
    details.dataset.animating = 'true';

    if (this._prefersReducedMotion() || typeof Motion === 'undefined') {
      this._setAccordionOpenState(details, contentAnim, willOpen);
      delete details.dataset.animating;
      return;
    }

    const summaryHeight = summary.offsetHeight;
    const openDuration = 0.42;
    const closeDuration = 0.34;
    const easing = 'ease-in-out';

    try {
      if (willOpen) {
        details.setAttribute('open', '');
        details.classList.add('is-open');

        const targetHeight = content.scrollHeight;
        details.style.height = `${summaryHeight}px`;
        contentAnim.style.height = '0px';
        contentAnim.style.opacity = '0';

        await Motion.timeline([
          [details, { height: [`${summaryHeight}px`, `${summaryHeight + targetHeight}px`] }, { duration: openDuration, easing }],
          [contentAnim, { height: ['0px', `${targetHeight}px`], opacity: [0, 1] }, { duration: openDuration, at: '<', easing }],
        ]).finished;

        details.style.height = 'auto';
        contentAnim.style.height = 'auto';
      } else {
        const currentContentHeight = contentAnim.scrollHeight || content.scrollHeight;
        details.style.height = `${details.offsetHeight}px`;
        contentAnim.style.height = `${currentContentHeight}px`;

        await Motion.timeline([
          [contentAnim, { height: [`${currentContentHeight}px`, '0px'], opacity: [1, 0] }, { duration: closeDuration, easing }],
          [details, { height: [`${details.offsetHeight}px`, `${summaryHeight}px`] }, { duration: closeDuration, at: '<', easing }],
        ]).finished;

        details.removeAttribute('open');
        details.classList.remove('is-open');
        details.style.height = '';
        contentAnim.style.height = '0px';
        contentAnim.style.opacity = '0';
      }
    } finally {
      delete details.dataset.animating;
    }
  }

  _initMoreToggle() {
    const toggle = this.querySelector('[data-more-toggle]');
    const list = this.querySelector('[data-more-list]');
    if (!toggle || !list) return;

    const items = list.querySelectorAll('[data-accessory-item]');
    if (items.length <= this.moreLimit) {
      toggle.remove();
      return;
    }

    toggle.addEventListener('click', this._onToggleMore);
    this._moreExpanded = false;
  }

  _onToggleMore(event) {
    event.preventDefault();
    this._moreExpanded = !this._moreExpanded;
    const items = [...this.querySelectorAll('[data-more-list] [data-accessory-item]')];
    const toggle = this.querySelector('[data-more-toggle]');
    const showMore = toggle?.dataset.showMore || 'Show more';
    const showLess = toggle?.dataset.showLess || 'Show less';

    items.forEach((item, index) => {
      if (index >= this.moreLimit) {
        item.classList.toggle('product-accessory-item--hidden', !this._moreExpanded);
      }
    });

    if (toggle) toggle.textContent = this._moreExpanded ? showLess : showMore;
  }

  _onCheckboxChange(event) {
    const item = event.target.closest('[data-accessory-item]');
    if (!item) return;

    item.classList.toggle('is-selected', event.target.checked);
    item.classList.toggle('is-checked', event.target.checked);
    this._updateUI();
  }

  _onVariantSelectChange(event) {
    const item = event.target.closest('[data-accessory-item]');
    const option = event.target.selectedOptions[0];
    if (!item || !option) return;

    const checkbox = item.querySelector('.product-accessory-item__input');
    const available = option.dataset.available === 'true';
    item.dataset.price = option.dataset.price;
    item.dataset.salePrice = option.dataset.salePrice;
    item.dataset.available = String(available);
    item.classList.toggle('is-unavailable', !available);

    if (checkbox) {
      checkbox.value = option.value;
      checkbox.dataset.variantId = option.value;
      checkbox.disabled =
        !available || item.dataset.locked === 'true' || item.classList.contains('product-accessory-item--duplicate');
      if (!available) {
        checkbox.checked = false;
        item.classList.remove('is-selected', 'is-checked');
      }
    }

    this._updateUI();
  }

  _onVariantChange(event) {
    const variant = event?.data?.variant;
    if (!variant) return;

    this._mainVariantPrice = Number(variant.price) || 0;
    this._mainVariantId = String(variant.id);
    const model = this._variantModelMap[this._mainVariantId] || variant.options?.[0] || variant.option1;
    if (model && model !== this.activeModel) this._setActiveModel(model);
    this._updateUI();
    window.requestAnimationFrame(() => this._updateUI());
  }

  _onQuantityChange() {
    this._updateUI();
  }

  _setActiveModel(model) {
    if (!model) return;
    this.activeModel = model;

    this.querySelectorAll('[data-model-panel]').forEach((panel) => {
      const active = panel.dataset.modelPanel === model;
      panel.hidden = !active;
      panel.setAttribute('aria-hidden', String(!active));
      panel.querySelectorAll('.product-accessory-item__input').forEach((input) => {
        const item = input.closest('[data-accessory-item]');
        input.disabled = !active || item?.dataset.available !== 'true' || item?.dataset.locked === 'true';
      });
    });

    this._deduplicateSharedAccessories();
    this._updateUI();
  }

  _deduplicateSharedAccessories() {
    const activePanel = [...this.querySelectorAll('[data-model-panel]')].find(
      (panel) => panel.dataset.modelPanel === this.activeModel
    );
    const activeProductIds = new Set(
      [...(activePanel?.querySelectorAll('[data-accessory-item]') || [])].map((item) => item.dataset.productId)
    );

    this.querySelectorAll('[data-more-list] [data-accessory-item]').forEach((item) => {
      const duplicate = activeProductIds.has(item.dataset.productId);
      const input = item.querySelector('.product-accessory-item__input');
      item.classList.toggle('product-accessory-item--duplicate', duplicate);
      if (input) input.disabled = duplicate || item.dataset.available !== 'true';
    });
  }

  _getQuantity() {
    const quantity = parseInt(this._quantityInput?.value || '1', 10);
    return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
  }

  _getSelectedItems() {
    const activePanel = [...this.querySelectorAll('[data-model-panel]')].find(
      (panel) => panel.dataset.modelPanel === this.activeModel
    );
    const activeItems = activePanel ? [...activePanel.querySelectorAll('[data-accessory-item].is-checked')] : [];
    const sharedItems = [...this.querySelectorAll('[data-more-list] [data-accessory-item].is-checked')].filter(
      (item) => !item.classList.contains('product-accessory-item--duplicate')
    );

    return [...activeItems, ...sharedItems].filter((item) => item.dataset.available === 'true');
  }

  _calculateTotal() {
    const quantity = this._getQuantity();
    let total = this._mainVariantPrice * quantity;

    this._getSelectedItems().forEach((item) => {
      const salePrice = parseInt(item.dataset.salePrice || item.dataset.price || '0', 10) || 0;
      total += salePrice * quantity;
    });

    return total;
  }

  _updateButtonText(total) {
    const submitButton = this._form?.querySelector('.product-form__submit');
    const submitButtonText = submitButton?.querySelector('.add-to-cart-text__content, .add-to-cart-text');
    if (!submitButtonText || submitButton.disabled) return;
    const formatted = typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function'
      ? Shopify.formatMoney(total, window.money_format)
      : (total / 100).toFixed(2);
    submitButtonText.textContent = `${this.addToCartLabel} - ${formatted}`;
  }

  _updateCounts() {
    this.querySelectorAll('[data-selected-count]').forEach((element) => {
      const group = element.closest('.product-accessory-bundle__group');
      const count = group
        ? [...group.querySelectorAll('[data-accessory-item].is-checked')].filter(
            (item) => !item.classList.contains('product-accessory-item--duplicate')
          ).length
        : 0;
      const template = element.dataset.countTemplate || '[count] Selected';
      element.textContent = template.replace('[count]', count);
    });
  }

  _updateUI() {
    this._updateCounts();
    this._updateButtonText(this._calculateTotal());
  }

  _createBundleId() {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  }

  async _onFormSubmit(event) {
    const selected = this._getSelectedItems();
    if (selected.length === 0) return;

    event.preventDefault();
    event.stopPropagation();

    const productForm = event.target.closest('product-form-component');
    const submitButton = productForm?.querySelector('.product-form__submit');
    const spinner = submitButton?.querySelector('.loading__spinner');

    if (submitButton?.getAttribute('aria-disabled') === 'true') return;

    submitButton?.setAttribute('aria-disabled', 'true');
    submitButton?.classList.add('loading');
    spinner?.classList.remove('hidden');

    try {
      const mainVariantId = this._form?.querySelector('[name="id"]')?.value || this._mainVariantId;
      const quantity = this._getQuantity();
      const bundleId = this._createBundleId();
      const model = this.activeModel || this._variantModelMap[String(mainVariantId)] || '';
      const items = [
        {
          id: Number(mainVariantId),
          quantity,
          properties: {
            _askmy_bundle_id: bundleId,
            _askmy_bundle_role: 'parent',
            _askmy_bundle_model: model,
          },
        },
      ];

      selected.forEach((item) => {
        const checkbox = item.querySelector('.product-accessory-item__input');
        if (!checkbox?.value) return;
        items.push({
          id: Number(checkbox.value),
          quantity,
          properties: {
            _askmy_bundle_id: bundleId,
            _askmy_bundle_role: 'accessory',
            _askmy_bundle_parent_variant: String(mainVariantId),
            _askmy_bundle_model: model,
            _askmy_bundle_price_mode: item.dataset.priceMode || 'paid',
          },
        });
      });

      const cart = document.querySelector('cart-drawer');
      const body = { items };

      if (cart && typeof cart.getSectionsToRender === 'function') {
        body.sections = cart.getSectionsToRender().map((section) => section.id);
        body.sections_url = window.location.pathname;
      }

      const cartAddRoute = window.routes?.cart_add_url || '/cart/add';
      const addUrl = cartAddRoute.endsWith('.js') ? cartAddRoute : `${cartAddRoute}.js`;
      const response = await fetch(addUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(body),
      });
      const data = await response.json();

      if (!response.ok || data.status || data.errors || data.description) {
        const message = data.description || data.errors || data.message || response.statusText;
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'product-accessory-bundle',
            errors: message,
            message,
          });
        }
        productForm?.handleErrorMessage?.(message);
        return;
      }

      if (!cart) {
        window.location.href = window.routes?.cart_url || '/cart';
        return;
      }

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'product-accessory-bundle',
          cartData: data,
        });
      }

      if (typeof cart.renderContents === 'function') cart.renderContents(data);
      productForm?.handleErrorMessage?.();
    } catch (error) {
      console.error('[ProductAccessoryBundle] Add to cart failed.', error);
      productForm?.handleErrorMessage?.(error.message);
    } finally {
      submitButton?.classList.remove('loading');
      spinner?.classList.add('hidden');
      submitButton?.removeAttribute('aria-disabled');
    }
  }
}

if (!customElements.get('product-accessory-bundle')) {
  customElements.define('product-accessory-bundle', ProductAccessoryBundle);
}
